import { Image, Spinner } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { getResidenceTypeImage } from "../clients/KompasApi";
import { atom, useAtom } from "jotai";

export const cachedImagesAtom = atom<Record<number, string>>({});
const requestImagePromises: Record<number, Promise<string>> = {};

interface ResidenceTypeImageProps {
  id: number;
  fallback?: string;
  round?: boolean;
  size?: number;
}

export function ResidenceTypeImage({ id, fallback = "", round = false, size = 100 }: ResidenceTypeImageProps) {
  const [imageUrl, setImageUrl] = useState<string>();
  const [cachedImages, setCachedImages] = useAtom(cachedImagesAtom);

  useEffect(() => setImageUrl(cachedImages[id]), [id, cachedImages]);

    const loadImage = useCallback(() => {
    requestImagePromises[id] ??= getResidenceTypeImage({
      path: { residenceTypeId: id },
      parseAs: "stream",
    })
      .then((result) => result.response.blob())
      .then((blob) => URL.createObjectURL(blob))
      .catch((error) => {
        throw new Error(`Failed to load image for ID ${id}: ${error.message}`);
      })
      .finally(() => delete requestImagePromises[id]);

    return requestImagePromises[id];
  }, [id]);

  useEffect(() => {
    if (cachedImages.hasOwnProperty(id)) return;

    const abortController = new AbortController();

    loadImage().then((url) => {
      setCachedImages((prev) => ({ ...prev, [id]: url }));

      if (!abortController.signal.aborted) setImageUrl(url);
    });

    return () => abortController.abort("Component unmounted or ID changed");
  }, [id, loadImage, cachedImages, setCachedImages]);


  if (!imageUrl && !fallback) return <Spinner color="brand.blue.500" size="md" />;

  if (!imageUrl) {
    return <svg fill="none" height="100" viewBox="0 0 100 100" width="100" xmlns="http://www.w3.org/2000/svg">
      <text dominantBaseline="middle" fill="#ccc" fontSize="12" textAnchor="middle" x="50%" y="50%">
        {fallback}
      </text>
    </svg>;
  }

  return round ? (
    <Image
      borderRadius="50%"
      h={`${size * 0.75}px`}
      ml={`${size * 0.125}px`}
      mt={`${size * 0.125}px`}
      pos="absolute"
      src={imageUrl}
      w={`${size * 0.75}px`}
    />
  ) : (
    <Image
      bg="brand.gray.100"
      borderRadius={8}
      h={`${size}px`}
      loading="lazy"
      mr={4}
      src={imageUrl}
      w={`${size}px`}
    />
  );
}
